import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
import React from "react";
import { type UseFormRegisterReturn } from "react-hook-form";
import { IconButton } from "~/features/ui/icon-button";
import { FieldError } from "~/features/ui/form/field-error";
import CaretDown from "~/public/icons/caret-down.svg";
type Option = {
  label: string;
  value: string;
};
type Props = {
  options: Option[];
  label?: string;
  error?: string;
  register?: UseFormRegisterReturn;
  tooltip?: React.ReactNode;
};
export type SelectOption = {
  value: string;
  name: string;
};
const style = {
  "height": "6.4rem",
  "width": "100%",
  "appearance": "none",
  "borderRadius": "2px",
  "borderBottomWidth": "1px",
  "--tw-border-opacity": "1",
  "borderBottomColor": "rgb(227 227 240 / var(--tw-border-opacity))",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))",
  "paddingLeft": "1.6rem",
  "paddingRight": "1.6rem",
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(23 23 23 / var(--tw-text-opacity))",
  "&:disabled": {
    "pointerEvents": "none",
    "backgroundColor": "rgba(255, 255, 255, 0.2)",
    "--tw-text-opacity": "1",
    "color": "rgb(92 92 92 / var(--tw-text-opacity))"
  },
  "@media (min-width: 1024px)": {
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
};
export const Select = React.forwardRef<HTMLSelectElement, Props & React.ComponentPropsWithRef<"select">>((props, ref) => {
  const {
    label,
    error,
    register,
    options,
    tooltip,
    value,
    defaultValue,
    ...rest
  } = props;

  // A <select> must be either controlled (value) or uncontrolled (defaultValue),
  // never both. Callers like FilterBar pass a `value`, so honor that as
  // controlled; otherwise (e.g. react-hook-form `register` usage) fall back to
  // selecting the first option by default, preserving the prior behavior.
  const valueProps = value !== undefined ? {
    value
  } : {
    defaultValue: defaultValue ?? options[0]?.value
  };
  return <_StyledDiv>
        {(tooltip || label) && <_StyledDiv2>
            {label && <_StyledSpan>
                {label}
                {rest.required && <_StyledSpan2>*</_StyledSpan2>}
              </_StyledSpan>}
            {tooltip}
          </_StyledDiv2>}
        <_StyledDiv3>
          <_StyledSelect ref={ref} {...valueProps} {...rest} {...register} $_css={[style, Boolean(error) && {
        "borderColor": "var(--color-functional-error)",
        "color": "var(--color-functional-error)",
        "&::placeholder": {
          "color": "var(--color-functional-error)"
        }
      }]}>
            {options.map((option, idx) => <option key={idx} value={option.value}>
                {option.label}
              </option>)}
          </_StyledSelect>
          <_StyledDiv4>
            <IconButton as="div" fullHeight icon={{
          component: CaretDown
        }} />
          </_StyledDiv4>
          {error && <FieldError message={error} />}
        </_StyledDiv3>
      </_StyledDiv>;
});
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-2xy6xp-0"
})({
  "display": "flex",
  "flexDirection": "column"
});
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-2xy6xp-1"
})({
  "marginBottom": "1.6rem",
  "display": "flex",
  "alignItems": "center",
  "gap": "0.8rem",
  "--tw-text-opacity": "1",
  "color": "rgb(92 92 92 / var(--tw-text-opacity))"
});
var _StyledSpan = _styled3("span").withConfig({
  componentId: "sc-sf-2xy6xp-2"
})({
  "display": "block",
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledSpan2 = _styled4("span").withConfig({
  componentId: "sc-sf-2xy6xp-3"
})({
  "marginLeft": "0.8rem",
  "--tw-text-opacity": "1",
  "color": "rgb(75 46 248 / var(--tw-text-opacity))"
});
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-2xy6xp-4"
})({
  "position": "relative"
});
var _StyledSelect = _styled6("select").withConfig({
  componentId: "sc-sf-2xy6xp-5"
})`${p => p.$_css}`;
var _StyledDiv4 = /*#__PURE__*/_styled7("div").withConfig({
  displayName: "select___StyledDiv4",
  componentId: "sc-vcymrm-0"
})({
  "pointerEvents": "none",
  "position": "absolute",
  "top": "0",
  "bottom": "0",
  "right": "0"
});